/* O menu lateral dos dois acessos (spec 4.1).
 *
 * Só entra aqui o que tem tela. GEOedu e Financeiro entraram em 09/10/2026 (requisito 5 da
 * Amanda) COM tela própria: a de cada um mostra o estado vazio honesto enquanto a origem do
 * dado não existe (SSO do GEOedu; títulos do sistema financeiro). Um item que leva a lugar
 * nenhum é o mesmo beco que a Amanda apontou na esteira em 28/08 — por isso nada de link morto.
 *
 * A exceção é Chamados, que a spec manda mostrar BLOQUEADO de propósito: o item existe para o
 * cliente saber que o atendimento vem depois, e a frase diz quando abre. Não é promessa de tela.
 */

import type { Sessao } from "./api";
import { podeNaSessao } from "./sessao";

/** O que o menu mostra sob o item Chamados bloqueado; o motivo completo vem do servidor. */
export const QUANDO_ABRE_O_CHAMADOS = "Abre quando o primeiro módulo for repassado ao atendimento.";

export interface ItemDoMenu {
  rotulo: string;
  /** Agrupamento visual do console interno. */
  bloco?: "Implantação" | "Catálogos" | "Atendimento" | "Contas e acessos" | "Operação";
  /** Ausente = item bloqueado: aparece, não navega, e mostra `quandoAbre`. */
  para?: string;
  /** `true` = ativo só no endereço exato; senão também nas telas abaixo dele. */
  exato?: boolean;
  /** Subitem com recuo visual (a Agenda é filha de Implantação — spec 4.1). */
  subitem?: boolean;
  quandoAbre?: string;
  /** Sem a permissão o item não aparece: o menu não oferece o que o servidor vai negar. */
  permissao?: string;
  /** Some quando a implantação já foi encerrada. */
  somenteDuranteImplantacao?: boolean;
}

export const MENU_INTERNO: readonly ItemDoMenu[] = [
  { bloco: "Implantação", rotulo: "Carteira", para: "/admin/implantacoes" },
  {
    bloco: "Implantação",
    rotulo: "Nova implantação",
    para: "/admin/implantacoes/nova",
    exato: true,
    permissao: "implantacao:criar",
  },
  { bloco: "Catálogos", rotulo: "Recursos Plus", para: "/admin/catalogo", permissao: "catalogo:gerenciar" },
  {
    bloco: "Catálogos",
    rotulo: "Perguntas do kickoff",
    para: "/admin/catalogo-do-kickoff",
    permissao: "questionario:gerenciar",
  },
  { bloco: "Atendimento", rotulo: "Chamados", para: "/admin/chamados", permissao: "ticket:ver" },
  { bloco: "Atendimento", rotulo: "Base de conhecimento", para: "/admin/conhecimento", permissao: "conhecimento:editar" },
  { bloco: "Atendimento", rotulo: "WebChat", para: "/admin/webchat", permissao: "webchat:atender" },
  { bloco: "Atendimento", rotulo: "Configurar chamados", para: "/admin/configuracao-de-chamados", permissao: "ticket:configurar" },
  { bloco: "Atendimento", rotulo: "Canais", para: "/admin/canais", permissao: "canal:triagem" },
  { bloco: "Contas e acessos", rotulo: "Contas de clientes", para: "/admin/cadastros", permissao: "implantacao:editar" },
  {
    bloco: "Contas e acessos",
    rotulo: "Usuários internos",
    para: "/admin/usuarios-internos",
    permissao: "usuarios:gerenciar",
  },
  { bloco: "Contas e acessos", rotulo: "Perfis de acesso", para: "/admin/perfis", permissao: "perfil:gerenciar" },
  { bloco: "Contas e acessos", rotulo: "Saúde dos clientes", para: "/admin/sucesso", permissao: "sucesso:ver" },
  { bloco: "Operação", rotulo: "Relatórios", para: "/admin/relatorios", permissao: "ticket:gerenciar" },
  { bloco: "Operação", rotulo: "Painéis", para: "/admin/paineis", permissao: "painel:gerenciar" },
  { bloco: "Operação", rotulo: "Exportações", para: "/admin/exportacoes", permissao: "exportacao:gerar" },
  { bloco: "Operação", rotulo: "Automação", para: "/admin/automacao", permissao: "macro:usar" },
  { bloco: "Operação", rotulo: "Horas e aprovações", para: "/admin/horas", permissao: "horas:gerenciar" },
  { bloco: "Operação", rotulo: "Compromissos", para: "/admin/compromissos", permissao: "compromisso:gerenciar" },
  { bloco: "Operação", rotulo: "Mural de avisos", para: "/admin/avisos", permissao: "aviso:gerenciar" },
  { bloco: "Operação", rotulo: "Mural de feedback", para: "/admin/feedback", permissao: "feedback:ver" },
  { bloco: "Operação", rotulo: "Pesquisas", para: "/admin/pesquisas", permissao: "pesquisa:resultados" },
  { bloco: "Operação", rotulo: "Ativos", para: "/admin/ativos", permissao: "ativo:gerenciar" },
  { bloco: "Operação", rotulo: "Migração do Movidesk", para: "/admin/migracao", permissao: "migracao:ver" },
];

const MENU_DO_CLIENTE: readonly ItemDoMenu[] = [
  { rotulo: "Implantação", para: "/app", exato: true, somenteDuranteImplantacao: true },
  {
    rotulo: "Agenda",
    para: "/app/reunioes",
    subitem: true,
    somenteDuranteImplantacao: true,
  },
  /* Item 10 da Amanda: a tela Contrato é uma tela da matriz de acesso (`contrato:portal`) e, ao
     contrário da Agenda e dos módulos, não some no encerramento — o termo de encerramento de cada
     módulo repassado mora nela. */
  { rotulo: "Contrato", para: "/app/contrato", permissao: "contrato:portal" },
  { rotulo: "Materiais", para: "/app/materiais" },
  { rotulo: "Central de ajuda", para: "/app/ajuda", permissao: "conhecimento:ver" },
  /* Visibilidade por usuário (migration 0099_usuarios_e_perfis, protótipo v9 telas 9 e 16): cada
     um é uma "tela" da matriz de acesso. A sessão já traz a permissão efetiva — perfil mais o
     ajuste por pessoa —, então o menu respeita o ajuste sem lógica própria. */
  { rotulo: "GEOedu", para: "/app/geoedu", permissao: "geoedu:ver" },
  { rotulo: "Financeiro", para: "/app/financeiro", permissao: "financeiro:ver" },
  { rotulo: "Usuários", para: "/app/usuarios", permissao: "usuarios:gerenciar" },
  { rotulo: "Chamados", para: "/app/chamados", permissao: "ticket:ver" },
];

/**
 * `chamadosBloqueados` (cliente): o item Chamados continua no menu, mas sem destino e com a frase
 * de quando abre — o item existe para o cliente saber que o atendimento vem depois (item 5 da
 * Amanda). Só o menu esconde o link: a recusa de abrir chamado é da API.
 */
export function itensDoMenu(
  lado: "cliente" | "interna",
  sessao: Sessao | null | undefined,
  implantacaoConcluida = false,
  modulos: readonly { chave: string; nome: string }[] = [],
  chamadosBloqueados = false,
): ItemDoMenu[] {
  const itens: readonly ItemDoMenu[] =
    lado === "interna"
      ? MENU_INTERNO
      : [
          MENU_DO_CLIENTE[0]!,
          ...modulos.map((modulo) => ({
            rotulo: modulo.nome,
            para: `/app/modulos/${encodeURIComponent(modulo.chave)}`,
            subitem: true,
            somenteDuranteImplantacao: true,
          })),
          ...MENU_DO_CLIENTE.slice(1),
        ];
  return itens
    .filter(
      (item) =>
        (!item.permissao || podeNaSessao(sessao, item.permissao)) &&
        !(implantacaoConcluida && item.somenteDuranteImplantacao),
    )
    .map((item) =>
      lado === "cliente" && chamadosBloqueados && item.para === "/app/chamados"
        ? { rotulo: item.rotulo, quandoAbre: QUANDO_ABRE_O_CHAMADOS }
        : item,
    );
}

/** Ativo no endereço exato ou, sem `exato`, também em qualquer tela abaixo dele. */
export function itemAtivo(item: ItemDoMenu, caminho: string, itens: readonly ItemDoMenu[]) {
  if (!item.para) return false;
  if (caminho === item.para) return true;
  if (item.exato || !caminho.startsWith(`${item.para}/`)) return false;
  // Um irmão mais específico que casa exatamente leva o destaque: em `/admin/implantacoes/nova`
  // o ativo é "Nova implantação", não "Implantações".
  return !itens.some((outro) => outro !== item && outro.para === caminho);
}
